import { useState, useEffect, useCallback } from 'react'
import {
  subscribeSyncQueue,
  triggerSync,
  type SyncQueueState
} from '../services/offline.service'

/** Sync status for the UI */
export type SyncStatusType = 'idle' | 'syncing' | 'pending' | 'error' | 'conflict'

/** Return type of useOnlineStatus hook */
export interface UseOnlineStatusReturn {
  /** Whether the browser is currently online */
  isOnline: boolean
  /** Whether the browser was recently offline */
  wasOffline: boolean
  /** Current sync status */
  syncStatus: SyncStatusType
  /** Number of pending operations */
  pendingCount: number
  /** Number of operations with conflicts */
  conflictCount: number
  /** Last sync timestamp */
  lastSyncTime: number | null
  /** Error message if sync failed */
  syncError: string | null
  /** Manually trigger a sync */
  manualSync: () => Promise<void>
  /** Whether a sync is in progress */
  isSyncing: boolean
}

/**
 * Determine the overall sync status from queue state
 */
function determineSyncStatus(state: SyncQueueState): SyncStatusType {
  if (state.isSyncing) return 'syncing'
  if (state.syncError) return 'error'

  const conflictCount = state.operations.filter(op => op.status === 'conflict').length
  if (conflictCount > 0) return 'conflict'

  const pendingCount = state.operations.filter(
    op => op.status === 'pending' || op.status === 'failed'
  ).length
  if (pendingCount > 0) return 'pending'

  return 'idle'
}

/**
 * Hook to track online/offline status and sync queue state
 */
export function useOnlineStatus(): UseOnlineStatusReturn {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )
  const [wasOffline, setWasOffline] = useState(false)

  // Sync queue state
  const [syncState, setSyncState] = useState<SyncQueueState>({
    operations: [],
    lastSyncTime: null,
    isSyncing: false,
    syncError: null
  })

  useEffect(() => {
    const handleOnline = (): void => {
      setIsOnline(true)
      // Keep wasOffline true for a moment to show "back online" message
      setTimeout(() => setWasOffline(false), 3000)
    }

    const handleOffline = (): void => {
      setIsOnline(false)
      setWasOffline(true)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Subscribe to sync queue state changes
  useEffect(() => {
    const unsubscribe = subscribeSyncQueue((state) => {
      setSyncState(state)
    })

    return unsubscribe
  }, [])

  const manualSync = useCallback(async (): Promise<void> => {
    if (!isOnline) return
    await triggerSync()
  }, [isOnline])

  // Compute derived values
  const syncStatus = determineSyncStatus(syncState)
  const pendingCount = syncState.operations.filter(
    op => op.status === 'pending' || op.status === 'failed'
  ).length
  const conflictCount = syncState.operations.filter(
    op => op.status === 'conflict'
  ).length

  return {
    isOnline,
    wasOffline,
    syncStatus,
    pendingCount,
    conflictCount,
    lastSyncTime: syncState.lastSyncTime,
    syncError: syncState.syncError,
    manualSync,
    isSyncing: syncState.isSyncing
  }
}

export default useOnlineStatus
